import { useMemo, useCallback, useState } from "react";
import { X } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { useQuery } from "@tanstack/react-query";
import { DynamicModuleService, SchemaService } from "@/services/APIs";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import { getEmployeeId } from "@/lib/storeManager";
import { Pagination } from "antd";

const ATTENDANCE_SCHEMA_NAME = "PTS Attendance";

interface AttendanceRecord {
  id: string;
  date: string;
  empname: string;
  checkin: string | null;
  checkout: string | null;
  breakin1: string | null;
  breakout1: string | null;
  breakin2: string | null;
  breakout2: string | null;
  totalhours: string | null;
  breckhours: string | null;
  workinghours: string | null;
  invalid: boolean;
  is_active: boolean;
}

interface AttendanceLogTabProps {
  selectedEmployeeId?: string | null;
}

const AttendanceLogTab = ({ selectedEmployeeId }: AttendanceLogTabProps) => {
  // Use selectedEmployeeId prop if provided, otherwise fallback to getEmployeeId()
  const employeeId = selectedEmployeeId ?? getEmployeeId();

  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(300);

  // Calculate default dates (6 days before today to today)
  const getDefaultDates = useCallback(() => {
    const today = new Date();
    const endDateStr = today.toISOString().split("T")[0]; // YYYY-MM-DD format
    const sixDaysInMs = 6 * 24 * 60 * 60 * 1000;
    const startDate = new Date(today.getTime() - sixDaysInMs);
    const startDateStr = startDate.toISOString().split("T")[0]; // YYYY-MM-DD format
    return {
      startDate: startDateStr,
      endDate: endDateStr,
    };
  }, []);

  const defaultDates = getDefaultDates();
  const [startDate, setStartDate] = useState<string>(defaultDates.startDate);
  const [endDate, setEndDate] = useState<string>(defaultDates.endDate);

  // Format date for API (YYYY-MM-DDTHH:mm:ss)
  // Start date: 00:00:00, End date: 23:59:59
  const formatDateForAPI = (dateString: string, isEndDate: boolean = false) => {
    if (!dateString) {
      return null;
    }
    const [year, month, day] = dateString.split("-").map(Number);
    if (isEndDate) {
      return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}T23:59:59`;
    } else {
      return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}T00:00:00`;
    }
  };

  // Fetch column grid data to find field names
  const { data: columnGridData } = useQuery({
    queryKey: ["columnGridData", ATTENDANCE_SCHEMA_NAME],
    queryFn: () => SchemaService.getColumnGridData(ATTENDANCE_SCHEMA_NAME),
  });

  // Fetch attendance data with pagination
  const { data: attendanceDataResponse, isLoading } = useQuery({
    queryKey: [
      "attendance-data",
      ATTENDANCE_SCHEMA_NAME,
      startDate,
      endDate,
      employeeId,
      currentPage,
      pageSize,
    ],
    queryFn: () => {
      const paramsFilter: any = {};

      // Add date filters
      if (startDate) {
        const formattedStartDate = formatDateForAPI(startDate, false);
        if (formattedStartDate) {
          paramsFilter.date__gte = formattedStartDate;
        }
      }
      if (endDate) {
        const formattedEndDate = formatDateForAPI(endDate, true);
        if (formattedEndDate) {
          paramsFilter.date__lte = formattedEndDate;
        }
      }

      // Add employee ID filter
      if (employeeId) {
        paramsFilter.empname__eq = employeeId;
      }

      paramsFilter.ordering = "-date";

      return DynamicModuleService.getItemsByPage(
        ATTENDANCE_SCHEMA_NAME,
        currentPage,
        pageSize,
        paramsFilter
      );
    },
    enabled: !!ATTENDANCE_SCHEMA_NAME,
  });

  // Extract data from response
  const attendanceRecords = useMemo(() => {
    if (!attendanceDataResponse) {
      return [];
    }
    if (Array.isArray(attendanceDataResponse)) {
      return attendanceDataResponse;
    }
    if (
      attendanceDataResponse.results &&
      Array.isArray(attendanceDataResponse.results)
    ) {
      return attendanceDataResponse.results;
    }
    return [];
  }, [attendanceDataResponse]);

  // Get total count from response
  const totalCount = useMemo(() => {
    if (!attendanceDataResponse) {
      return 0;
    }
    if (typeof attendanceDataResponse.count === "number") {
      return attendanceDataResponse.count;
    }
    // Fallback: if no count, use array length
    if (Array.isArray(attendanceDataResponse)) {
      return attendanceDataResponse.length;
    }
    if (
      attendanceDataResponse.results &&
      Array.isArray(attendanceDataResponse.results)
    ) {
      return attendanceDataResponse.results.length;
    }
    return 0;
  }, [attendanceDataResponse]);

  // Find field names from schema
  const findFieldName = useCallback(
    (searchTerms: string[]) => {
      if (!columnGridData?.grid) {
        return null;
      }
      for (const field of columnGridData.grid) {
        const fieldName = field.field_name.toLowerCase();
        const fieldTitle = field.field_title?.toLowerCase() ?? "";
        if (
          searchTerms.some(
            term =>
              fieldName.includes(term.toLowerCase()) ??
              fieldTitle.includes(term.toLowerCase())
          )
        ) {
          return field.field_name;
        }
      }
      return null;
    },
    [columnGridData]
  );

  // Get field names
  const dateField = findFieldName(["date", "attendance_date", "work_date"]);
  const empnameField = findFieldName([
    "empname",
    "employee_name",
    "employee",
    "name",
  ]);
  const checkinField = findFieldName(["checkin", "check_in", "check-in"]);
  const checkoutField = findFieldName(["checkout", "check_out", "check-out"]);
  const breakin1Field = findFieldName(["breakin1", "break_in1", "break_in_1"]);
  const breakout1Field = findFieldName([
    "breakout1",
    "break_out1",
    "break_out_1",
  ]);
  const breakin2Field = findFieldName(["breakin2", "break_in2", "break_in_2"]);
  const breakout2Field = findFieldName([
    "breakout2",
    "break_out2",
    "break_out_2",
  ]);
  const totalhoursField = findFieldName([
    "totalhours",
    "total_hours",
    "total_hours_worked",
  ]);
  const breckhoursField = findFieldName([
    "breckhours",
    "break_hours",
    "breakhours",
    "total_break_hours",
  ]);
  const workinghoursField = findFieldName([
    "workinghours",
    "working_hours",
    "productive_hours",
    "productivehours",
  ]);
  const invalidField = findFieldName(["invalid", "is_invalid"]);

  // Map raw data to AttendanceRecord interface
  const mappedRecords = useMemo(() => {
    return attendanceRecords.map((record: any): AttendanceRecord => {
      const recordId =
        record?.id ?? record?.ID ?? record?.Id ?? record?._id ?? "";

      return {
        id: String(recordId),
        date:
          (dateField && record[dateField]) ??
          record.date ??
          record.attendance_date ??
          "",
        empname:
          (empnameField && record[empnameField]) ??
          record.empname ??
          record.employee_name ??
          "",
        checkin:
          (checkinField && record[checkinField]) ??
          record.checkin ??
          record.check_in ??
          null,
        checkout:
          (checkoutField && record[checkoutField]) ??
          record.checkout ??
          record.check_out ??
          null,
        breakin1:
          (breakin1Field && record[breakin1Field]) ??
          record.breakin1 ??
          record.break_in1 ??
          null,
        breakout1:
          (breakout1Field && record[breakout1Field]) ??
          record.breakout1 ??
          record.break_out1 ??
          null,
        breakin2:
          (breakin2Field && record[breakin2Field]) ??
          record.breakin2 ??
          record.break_in2 ??
          null,
        breakout2:
          (breakout2Field && record[breakout2Field]) ??
          record.breakout2 ??
          record.break_out2 ??
          null,
        totalhours:
          (totalhoursField && record[totalhoursField]) ??
          record.totalhours ??
          record.total_hours ??
          null,
        breckhours:
          (breckhoursField && record[breckhoursField]) ??
          record.breckhours ??
          record.break_hours ??
          null,
        workinghours:
          (workinghoursField && record[workinghoursField]) ??
          record.workinghours ??
          record.working_hours ??
          null,
        invalid:
          (invalidField && record[invalidField]) ?? record.invalid ?? false,
        is_active: record.is_active ?? true,
      };
    });
  }, [
    attendanceRecords,
    dateField,
    empnameField,
    checkinField,
    checkoutField,
    breakin1Field,
    breakout1Field,
    breakin2Field,
    breakout2Field,
    totalhoursField,
    breckhoursField,
    workinghoursField,
    invalidField,
  ]);
  // Helper function to format date from ISO string to DD-MM-YYYY
  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch {
      return dateString;
    }
  };

  // Helper function to format time from HH:MM:SS to HH:MM AM/PM
  const formatTime = (timeString: string | null): string => {
    if (!timeString) {
      return "-";
    }
    try {
      const [hours, minutes] = timeString.split(":");
      const hour = parseInt(hours, 10);
      const minute = minutes || "00";
      const period = hour >= 12 ? "PM" : "AM";
      const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
      return `${String(displayHour).padStart(2, "0")}:${minute} ${period}`;
    } catch {
      return timeString;
    }
  };

  // Helper function to format time as HH:MM:SS or HH:MM (display as backend sends)
  const formatTimeDisplay = (timeString: string | null): string => {
    if (!timeString) {
      return "-";
    }
    try {
      // If already in HH:MM:SS format, return as is (or format to HH:MM)
      const parts = timeString.split(":");
      if (parts.length >= 2) {
        const hours = parts[0];
        const minutes = parts[1];
        // Return as HH:MM format (remove seconds if present)
        return `${hours}:${minutes}`;
      }
      return timeString;
    } catch {
      return timeString || "-";
    }
  };

  // Helper function to format break time as hours and minutes (e.g., "1h 30m", "30m", "1h")
  const formatBreakTime = (timeString: string | null): string => {
    if (!timeString) {
      return "0h";
    }
    try {
      const [hours, minutes] = timeString.split(":").map(Number);
      const h = hours || 0;
      const m = minutes || 0;

      if (h === 0 && m === 0) {
        return "0h";
      }
      if (h === 0) {
        return `${m}m`;
      }
      if (m === 0) {
        return `${h}h`;
      }
      return `${h}h ${m}m`;
    } catch {
      return timeString;
    }
  };

  // Helper function to determine status based on invalid field
  const getStatus = (record: AttendanceRecord): string => {
    // If invalid is false → Absent, if invalid is true → Present
    return record.invalid ? "Absent" : "Present";
  };

  // Helper function to get status badge styling
  const getStatusBadgeClass = (status: string): string => {
    switch (status) {
      case "Present":
        return "rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-700";
      case "Absent":
        return "rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-700";
      default:
        return "rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700";
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="rounded-xl border bg-white">
      <div className="flex items-center justify-between border-b p-3 md:p-4">
        <div className="flex flex-wrap items-center gap-3">
          <h3 className="text-base font-semibold text-gray-900">
            Attendance History
          </h3>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative inline-flex items-center">
              <label
                htmlFor="start-date"
                className="mr-1 text-xs text-gray-600"
              >
                Start:
              </label>
              <input
                type="date"
                id="start-date"
                value={startDate}
                onChange={e => {
                  setStartDate(e.target.value);
                  setCurrentPage(1); // Reset to first page when date changes
                }}
                className="border-border-input bg-background-input focus:border-primary focus:ring-primary rounded-lg border px-3 py-1 pr-8 text-sm text-gray-900 focus:outline-none focus:ring-1"
              />
              {startDate && startDate !== defaultDates.startDate && (
                <button
                  onClick={() => {
                    const defaults = getDefaultDates();
                    setStartDate(defaults.startDate);
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  title="Reset to default"
                  type="button"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            <div className="relative inline-flex items-center">
              <label htmlFor="end-date" className="mr-1 text-xs text-gray-600">
                End:
              </label>
              <input
                type="date"
                id="end-date"
                value={endDate}
                onChange={e => {
                  setEndDate(e.target.value);
                  setCurrentPage(1); // Reset to first page when date changes
                }}
                className="border-border-input bg-background-input focus:border-primary focus:ring-primary rounded-lg border px-3 py-1 pr-8 text-sm text-gray-900 focus:outline-none focus:ring-1"
              />
              {endDate && endDate !== defaultDates.endDate && (
                <button
                  onClick={() => {
                    const today = new Date();
                    setEndDate(today.toISOString().split("T")[0]);
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  title="Reset to today"
                  type="button"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>
        </div>
        <span className="text-sm text-gray-500">
          {mappedRecords.length} Records
        </span>
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-gray-50">
              <TableHead className="text-center font-semibold text-gray-700">
                Date
              </TableHead>
              <TableHead className="text-center font-semibold text-gray-700">
                Check-In
              </TableHead>
              <TableHead className="text-center font-semibold text-gray-700">
                Check-Out
              </TableHead>
              <TableHead className="text-center font-semibold text-gray-700">
                Total Hrs
              </TableHead>
              <TableHead className="text-center font-semibold text-gray-700">
                Break
              </TableHead>
              <TableHead className="text-center font-semibold text-gray-700">
                Productive
              </TableHead>
              <TableHead className="text-center font-semibold text-gray-700">
                Status
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {mappedRecords.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="py-8 text-center text-gray-500"
                >
                  No attendance records found
                </TableCell>
              </TableRow>
            ) : (
              mappedRecords.map((record: AttendanceRecord) => {
                const status = getStatus(record);
                const totalHours = formatTimeDisplay(record.totalhours);
                const breakTime = formatBreakTime(record.breckhours);
                const productiveHours = formatTimeDisplay(record.workinghours);

                return (
                  <TableRow key={record.id} className="hover:bg-gray-50">
                    <TableCell className="text-center text-gray-900">
                      {formatDate(record.date)}
                    </TableCell>
                    <TableCell className="text-center text-gray-700">
                      {formatTime(record.checkin)}
                    </TableCell>
                    <TableCell className="text-center font-medium text-teal-600">
                      {formatTime(record.checkout)}
                    </TableCell>
                    <TableCell className="text-center font-medium text-blue-600">
                      {totalHours !== "-" ? `${totalHours}h` : "-"}
                    </TableCell>
                    <TableCell className="text-center font-medium text-orange-600">
                      {breakTime}
                    </TableCell>
                    <TableCell className="text-center font-medium text-green-600">
                      {productiveHours !== "-" ? `${productiveHours}h` : "-"}
                    </TableCell>
                    <TableCell className="text-center">
                      <span className={getStatusBadgeClass(status)}>
                        {status}
                      </span>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
      {/* Pagination */}
      <div className="flex items-center justify-end border-t p-4">
        <Pagination
          current={currentPage}
          pageSize={pageSize}
          total={totalCount}
          onChange={(page, size) => {
            if (size && size !== pageSize) {
              setCurrentPage(1);
              setPageSize(size);
            } else {
              setCurrentPage(page);
            }
          }}
          onShowSizeChange={(_current, size) => {
            setCurrentPage(1);
            setPageSize(size);
          }}
          showSizeChanger
          showQuickJumper
          showTotal={(total, range) =>
            `${range[0]}-${range[1]} of ${total} items`
          }
          pageSizeOptions={["300", "500"]}
          disabled={isLoading}
        />
      </div>
    </div>
  );
};

export default AttendanceLogTab;
